import { Accordion, Box, Input, Stack, TextInput } from "@mantine/core";
import { useSetState } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import { showOldNotification as showNotification } from "@rpi/openapi-core";
import { GraphQL, Rest } from "@rpi/openapi-api";
import React from "react";
import {
  deriveOrderFiles,
  fetchTemplateMetadata,
  resolveProductLabel,
  templateApiBaseUrl,
} from "../utils/templateApi.util";
import ReactJson from "react-json-view";
import {
  ApiAccordion,
  ApiAccordionForm,
  ApiAccordionLabel,
  ApiAccordionLabelProps,
  ApiMethod,
} from "../components/accordions/ApiAccordion";
import { AppPage } from "../components/AppPage";
import { SelectApiCredential } from "../components/selects/SelectApiCredential";
import { SelectCustomerProduct } from "../components/selects/SelectCustomerProduct";
import { useLoadingCallback } from "../hooks/useLoadingCallback";
import { formatAuthHeader, generateJsonCurl, propPlaceholder } from "../utils/misc.util";
import { isEmptyString } from "../utils/type.util";
import { AxiosResponse, AxiosError } from "axios";
import { useAuthUser } from "../hooks/useAuthUser";
const requests: Array<ApiAccordionLabelProps & { element: React.FC<ApiRequestProps> }> = [
  {
    method: ApiMethod.GET,
    path: "/orders/:customerOrderId",
    description: "Get order by id",
    element: GetOrderById,
  },
  {
    method: ApiMethod.POST,
    path: "/orders/create",
    description: "Create an order",
    element: PostCreateOrder,
  },
];

export const ApiExamplesView: React.FC = () => {
  const user = useAuthUser();
  const apiCredentialsQuery = GraphQL.useListApiCredentialsQuery();
  const apiCredentials = apiCredentialsQuery.data?.listApiCredentials.filter(
    (credential) => user.isSandbox === credential.isSandbox
  );
  const customerProductsQuery = GraphQL.useListCustomerProductsQuery();

  const error = React.useMemo(
    () => apiCredentialsQuery.error || customerProductsQuery.error,
    [apiCredentialsQuery, customerProductsQuery]
  );

  const isLoading = React.useMemo(
    () => apiCredentialsQuery.isLoading || customerProductsQuery.isLoading,
    [apiCredentialsQuery, customerProductsQuery]
  );

  const customerProducts = React.useMemo(
    () => customerProductsQuery.data?.listCustomerProducts,
    [customerProductsQuery]
  );

  const items = React.useMemo(
    () =>
      requests.map((request, i) => (
        <Accordion.Item key={`request-example-${i}`} value={i.toString()}>
          <Accordion.Control children={<ApiAccordionLabel {...request} />} />
          <Accordion.Panel
            children={React.createElement(request.element, { request, apiCredentials, customerProducts })}
          />
        </Accordion.Item>
      )),
    [apiCredentials, customerProducts]
  );

  return (
    <AppPage path={["API Examples"]} isLoading={isLoading || !!error}>
      <div data-testid="api-examples-view">
        <ApiAccordion chevronPosition="right" children={items} />
      </div>
    </AppPage>
  );
};

interface ApiRequestProps {
  request: (typeof requests)[0];
  apiCredentials?: GraphQL.ListApiCredentialsQuery["listApiCredentials"];
  customerProducts?: GraphQL.ListCustomerProductsQuery["listCustomerProducts"];
}

function GetOrderById({ request, apiCredentials }: ApiRequestProps) {
  const { isLoading, loadingCallback } = useLoadingCallback();
  const [response, setResponse] = React.useState<{
    data?: Awaited<ReturnType<typeof Rest.getOrderById>>["data"];
    error?: any;
  }>({});

  const [state, setState] = useSetState({ orderId: "" });
  const [selectedCredentialKey, setSelectedCredentialKey] = React.useState<string>("");
  const selectedCredential = apiCredentials?.find((credential) => credential.apiKey === selectedCredentialKey);
  const selectedCredentialValue = selectedCredential?.apiKey ?? "";
  const authHeader = selectedCredential
    ? formatAuthHeader(selectedCredential.apiKey, selectedCredential.sharedSecret)
    : "";

  const curl = React.useMemo(() => {
    return generateJsonCurl({
      method: request.method,
      url: `${import.meta.env.VITE_PUBLIC_PRINTAPI_ENDPOINT_URL}/orders/${propPlaceholder(
        state.orderId,
        ":customerOrderId"
      )}`,
      headers: {
        accept: "application/json",
        Authorization: propPlaceholder(authHeader, "Basic base64(apiKey:sharedSecret)"),
      },
    });
  }, [request, authHeader, state]);

  const handleSubmit = React.useCallback(() => {
    loadingCallback(
      async () =>
        await Rest.getOrderById(state.orderId, authHeader)
          .then((res: any) => {
            showNotification({
              title: "Fetched order.",
              message: `Got order by id '${res.data?.order.customerOrderId}'.`,
              color: "green",
            });
            setResponse({ data: res.data });
          })
          .catch((error: AxiosError) => {
            console.error(error);
            showNotification({
              title: "Failed to fetch order.",
              message: (error?.response?.data as Record<string, any>)?.errorDescription ?? error.message,
              color: "red",
            });
            setResponse({ error: error?.response?.data ?? error });
          })
    );
  }, [state, authHeader, loadingCallback]);

  return (
    <ApiAccordionForm
      curl={curl}
      response={response}
      buttonProps={{
        loading: isLoading,
        disabled: isEmptyString(authHeader) || isEmptyString(state.orderId),
        onClick: handleSubmit,
      }}
    >
      <TextInput
        label="Customer Order Id"
        value={state.orderId}
        onChange={(e) => setState({ orderId: e.currentTarget.value || "" })}
      />
      <SelectApiCredential
        required
        data={apiCredentials}
        value={selectedCredentialValue}
        onChange={(value) => setSelectedCredentialKey(value ?? "")}
      />
    </ApiAccordionForm>
  );
}

export function PostCreateOrder({ request, apiCredentials, customerProducts }: ApiRequestProps) {
  const { isLoading, loadingCallback } = useLoadingCallback();
  const [response, setResponse] = React.useState<{
    data?: GraphQL.CreateOrderResponse;
    error?: any;
  }>({});

  const [selectedCredentialKey, setSelectedCredentialKey] = React.useState<string>("");
  const selectedCredential = apiCredentials?.find((credential) => credential.apiKey === selectedCredentialKey);
  const selectedCredentialValue = selectedCredential?.apiKey ?? "";
  const authHeader = selectedCredential
    ? formatAuthHeader(selectedCredential.apiKey, selectedCredential.sharedSecret)
    : "";
  const [customerProduct, setCustomerProduct] = React.useState<string>("{sku}");

  const [data, setData] = React.useState<any>({
    payload: "{custom_payload}",
    currency: "USD",
    shippingClassification: "standard",
    webhookUrl:
      "https://notification-testing-service.builder.blurb.com/notification/rpi-open-api-test/http-code-200/dummy",
    destination: {
      name: "John Doe",
      company: "RPI print",
      address1: "3325 S 116th St",
      city: "Tukwila",
      state: "Washington",
      postal: "98168",
      country: "US",
      phone: "(206) 905 5999",
      email: "shipping.dept@rpiprint.com",
    },
    orderItems: [
      {
        sku: "{sku}",
        quantity: 1,
        retailPrice: "12.3",
        itemDescription: "{item_description}",
        product: {
          coverUrl: "{cover_url}",
          gutsUrl: "{guts_url}",
        },
      },
    ],
  });

  const templateBaseUrl = templateApiBaseUrl();
  const selectedProduct = customerProducts?.find((product) => product.customerSku === customerProduct);
  const manufacturingSku = selectedProduct?.openapiProduct?.openapiSku;
  const productLabel = resolveProductLabel(selectedProduct?.name, selectedProduct?.description);

  const setProductFiles = (product: Record<string, string>, sku?: string) =>
    setData((prev: any) => ({
      ...prev,
      orderItems: prev.orderItems.map((item: any) => ({
        ...item,
        ...(sku === undefined ? {} : { sku }),
        product: { ...item.product, ...product },
      })),
    }));

  // Cover requires a page count; the metadata endpoint supplies the product minimum.
  useQuery(
    ["template-metadata", manufacturingSku],
    () => fetchTemplateMetadata(templateBaseUrl ?? "", manufacturingSku ?? ""),
    {
      enabled: !!manufacturingSku && !!templateBaseUrl,
      onSuccess: (metadata) => {
        if (!manufacturingSku || !templateBaseUrl) return;
        const { fileUrls } = deriveOrderFiles({
          base: templateBaseUrl,
          manufacturingSku,
          label: productLabel,
          metadata,
          files: ["coverUrl", "gutsUrl"],
        });
        setProductFiles({
          coverUrl: fileUrls.coverUrl ?? "{cover_url}",
          gutsUrl: fileUrls.gutsUrl ?? "{guts_url}",
        });
      },
      onError: () =>
        showNotification({
          title: "Failed to load template files.",
          message: "Could not fetch the product's template metadata; cover and guts URLs were left unset.",
          color: "red",
        }),
    }
  );

  const orderItem = data.orderItems?.[0];
  const coverUrlValue = orderItem?.product?.coverUrl ?? "{cover_url}";
  const gutsUrlValue = orderItem?.product?.gutsUrl ?? "{guts_url}";

  const curl = React.useMemo(() => {
    return generateJsonCurl({
      method: request.method,
      url: `${import.meta.env.VITE_PUBLIC_PRINTAPI_ENDPOINT_URL}/orders/create`,
      headers: {
        accept: "application/json",
        Authorization: propPlaceholder(authHeader, "Basic base64(apiKey:sharedSecret)"),
      },
      data: data || {},
    });
  }, [request, authHeader, data]);

  const handleSubmit = React.useCallback(() => {
    loadingCallback(
      async () =>
        await Rest.createOrder(data, authHeader)
          .then((res: AxiosResponse<GraphQL.CreateOrderResponse>) => {
            showNotification({
              title: "New order.",
              message: `Created new order '${res.data.customerOrderId}'.`,
              color: "green",
            });
            setResponse({ data: res.data });
          })
          .catch((error: AxiosError) => {
            console.error(error);
            showNotification({
              title: "Failed to create order.",
              message: (error?.response?.data as Record<string, any>)?.errorDescription ?? error.message,
              color: "red",
            });
            setResponse({ error: error?.response?.data ?? error });
          })
    );
  }, [data, authHeader, loadingCallback]);

  return (
    <ApiAccordionForm
      curl={curl}
      response={response}
      buttonProps={{
        loading: isLoading,
        disabled: isEmptyString(authHeader) || isEmptyString(customerProduct),
        onClick: handleSubmit,
      }}
      jsonInput={
        <Input.Wrapper label="Data" required>
          <Box p={8} sx={{ maxHeight: 400, overflowY: "auto", border: "1px solid #ced4da", borderRadius: 4 }}>
            <ReactJson
              src={data}
              onEdit={({ updated_src }) => setData(updated_src)}
              onAdd={({ updated_src }) => setData(updated_src)}
              onDelete={({ updated_src }) => setData(updated_src)}
              name={null}
              displayDataTypes={false}
              style={{ fontSize: 14 }}
            />
          </Box>
        </Input.Wrapper>
      }
    >
      <Stack spacing="sm">
        <SelectApiCredential
          required
          data={apiCredentials}
          value={selectedCredentialValue}
          onChange={(value) => setSelectedCredentialKey(value ?? "")}
        />
        <SelectCustomerProduct
          required
          data={customerProducts?.slice().sort((cp1, cp2) => cp1.openapiProductId - cp2.openapiProductId)}
          value={customerProduct}
          onChange={(value) => {
            const customerSku = value || "";
            setCustomerProduct(customerSku);
            setProductFiles({ coverUrl: "{cover_url}", gutsUrl: "{guts_url}" }, customerSku);
          }}
        />
      </Stack>
      <Stack spacing="sm">
        <TextInput label="Cover PDF" required readOnly data-testid="cover-pdf-url" value={coverUrlValue} />
        <TextInput label="Guts PDF" required readOnly data-testid="guts-pdf-url" value={gutsUrlValue} />
      </Stack>
    </ApiAccordionForm>
  );
}
